import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Clock, ArrowRight, CheckCircle2, Calculator, Eye, Hash, Timer } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import Logo from "@/components/Logo";
import VirtualAbacus from "@/components/VirtualAbacus";
import WorksheetGrid from "@/components/WorksheetGrid";

function fmtElapsed(sec) {
  const m = Math.floor(sec / 60).toString().padStart(2, "0");
  const s = Math.floor(sec % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}

const TYPE_META = {
  arithmetic: { label: "Math drill", icon: Calculator },
  read:       { label: "Show the number", icon: Hash },
  represent:  { label: "Read the beads",   icon: Eye },
  assessment: { label: "Timed exam", icon: Timer },
};

export default function Practice() {
  const { attemptId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [current, setCurrent] = useState(0);
  const [typedAnswer, setTypedAnswer] = useState("");
  const [abacusValue, setAbacusValue] = useState(0);
  const [answers, setAnswers] = useState({});
  const [elapsed, setElapsed] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [questionRemaining, setQuestionRemaining] = useState(null);
  const questionStartedAtRef = useRef(null);
  const abacusRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/student/attempts/${attemptId}`);
        if (data.attempt.status === "completed") {
          navigate(`/result/${attemptId}`, { replace: true });
          return;
        }
        setData(data);
        const map = {};
        (data.attempt.answers || []).forEach((a) => { map[a.question_id] = a.answer; });
        setAnswers(map);
        const firstUnansweredIdx = data.questions.findIndex((q) => map[q.id] == null);
        setCurrent(firstUnansweredIdx === -1 ? 0 : firstUnansweredIdx);
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        navigate("/student", { replace: true });
      } finally {
        setLoading(false);
      }
    })();
  }, [attemptId, navigate]);

  useEffect(() => {
    if (!data?.attempt?.started_at) return;
    const start = new Date(data.attempt.started_at).getTime();
    const tick = () => setElapsed(Math.max(0, Math.floor((Date.now() - start) / 1000)));
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [data]);

  const exercise = data?.exercise;
  const type = exercise?.type || "arithmetic";
  const isAssessment = type === "assessment";
  const secondsPerQuestion = exercise?.seconds_per_question || 30;
  const questions = data?.questions || [];
  const total = questions.length;
  const q = questions[current];

  // Reset per-question typed answer + abacus value when switching question
  useEffect(() => {
    if (!q) return;
    const saved = answers[q.id];
    if (type === "read") {
      setAbacusValue(saved != null ? Number(saved) : 0);
      setTypedAnswer("");
    } else if (type === "represent") {
      setAbacusValue(q.starting_number || 0);
      setTypedAnswer(saved != null ? String(saved) : "");
    } else {
      setTypedAnswer(saved != null ? String(saved) : "");
      setAbacusValue(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, q?.id, type]);

  const meta = TYPE_META[type] || TYPE_META.arithmetic;
  const TypeIcon = meta.icon;
  const useAbacusAsAnswer = type === "read";
  const abacusRods = type === "arithmetic" ? 4 : 13;
  const abacusOnes = type === "arithmetic" ? 3 : 6;
  const abacusReadOnly = type === "represent";
  const abacusInitial = type === "represent" ? (q?.starting_number || 0) : 0;

  // Per-question countdown for assessments — auto-skip on timeout
  useEffect(() => {
    if (!isAssessment || !q || !data?.attempt) return;
    // Base time: server's current_question_started_at when we entered this Q; else now
    const serverStart = data.attempt.current_question_started_at;
    let base;
    if (current === 0 && serverStart) {
      base = new Date(serverStart).getTime();
    } else if (questionStartedAtRef.current) {
      base = questionStartedAtRef.current;
    } else {
      base = Date.now();
      questionStartedAtRef.current = base;
    }
    if (current !== 0 && !questionStartedAtRef.current) {
      questionStartedAtRef.current = Date.now();
      base = questionStartedAtRef.current;
    }
    const tick = () => {
      const remaining = Math.max(0, secondsPerQuestion - Math.floor((Date.now() - base) / 1000));
      setQuestionRemaining(remaining);
      if (remaining <= 0) {
        clearInterval(t);
        // Auto-skip: save skipped answer and advance
        (async () => {
          try {
            await api.post(`/student/attempts/${attemptId}/answer`, { question_id: q.id, is_skipped: true });
            toast.info("Time's up — skipped");
            if (current + 1 < total) {
              questionStartedAtRef.current = Date.now();
              setCurrent(current + 1);
            } else {
              await api.post(`/student/attempts/${attemptId}/submit`);
              navigate(`/result/${attemptId}`, { replace: true });
            }
          } catch (err) { /* ignore */ }
        })();
      }
    };
    tick();
    const t = setInterval(tick, 250);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAssessment, q?.id, current, secondsPerQuestion]);

  // Reset the per-Q timer base whenever we transition to a new question
  useEffect(() => {
    if (isAssessment) questionStartedAtRef.current = Date.now();
  }, [current, isAssessment]);

  const goNext = async () => {
    if (!q) return;
    let parsed;
    if (useAbacusAsAnswer) {
      parsed = Number(abacusValue);
      if (parsed === 0 && q.starting_number !== 0) {
        toast.error("Move some beads to show the number on the abacus.");
        return;
      }
    } else {
      if (typedAnswer.trim() === "") { toast.error("Please enter a number."); return; }
      parsed = parseInt(typedAnswer, 10);
      if (Number.isNaN(parsed)) { toast.error("Answer must be a number."); return; }
    }
    try {
      setSubmitting(true);
      await api.post(`/student/attempts/${attemptId}/answer`, { question_id: q.id, answer: parsed });
      setAnswers((prev) => ({ ...prev, [q.id]: parsed }));
      if (current + 1 < total) {
        setCurrent(current + 1);
      } else {
        await api.post(`/student/attempts/${attemptId}/submit`);
        toast.success("Practice complete!");
        navigate(`/result/${attemptId}`, { replace: true });
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setSubmitting(false); }
  };

  const goPrev = () => { if (current > 0) setCurrent(current - 1); };

  if (loading || !data) {
    return <div className="min-h-screen flex items-center justify-center bg-canvas text-stone font-heading">Loading practice…</div>;
  }

  if (exercise?.presentation_mode === "grid" && !isAssessment) {
    return (
      <WorksheetGrid
        attemptId={attemptId}
        startedAt={data.attempt.started_at}
        exercise={exercise}
        questions={questions}
        initialAnswers={answers}
      />
    );
  }

  if (!q) {
    return <div className="min-h-screen flex items-center justify-center bg-canvas text-stone font-heading">Loading practice…</div>;
  }

  return (
    <div className="min-h-screen bg-canvas flex flex-col">
      <header className="sticky top-0 z-30 border-b border-cloud bg-white/85 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
          <Logo />
          <div className="flex items-center gap-3">
            {isAssessment && questionRemaining !== null && (
              <div
                data-testid="question-timer"
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full font-mono font-bold tabular-nums border-2 ${
                  questionRemaining <= 5 ? "bg-watermelon-light text-watermelon border-watermelon animate-pulse" : "bg-lemon/40 text-ink border-lemon"
                }`}
              >
                <Timer className="w-4 h-4" /> {questionRemaining}s
              </div>
            )}
            <div data-testid="timer" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-sky-light border-2 border-sky/20 font-mono font-bold text-sky-hover tabular-nums">
              <Clock className="w-4 h-4" /> {fmtElapsed(elapsed)}
            </div>
            <button data-testid="exit-practice-button" onClick={() => navigate("/student")} className="text-sm font-bold text-stone hover:text-ink px-3 py-2 rounded-full">Exit</button>
          </div>
        </div>
      </header>

      <main className="flex-1">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
          <div className="mb-6">
            <div className="flex items-baseline justify-between mb-2">
              <div>
                <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-sky-hover mb-1">
                  <TypeIcon className="w-4 h-4" /> {meta.label}
                </div>
                <div className="text-xs font-bold uppercase tracking-wider text-stone">{exercise.title}</div>
                <div data-testid="question-progress" className="font-heading text-2xl sm:text-3xl font-bold text-ink">Question {current + 1} of {total}</div>
              </div>
              <div className="text-sm font-semibold text-stone">{Object.keys(answers).length} answered</div>
            </div>
            <div className="h-3 bg-white rounded-full overflow-hidden border border-cloud">
              <div className="h-full bg-gradient-to-r from-watermelon to-lemon transition-all" style={{ width: `${((current + (answers[q.id] != null ? 1 : 0)) / total) * 100}%` }} />
            </div>
          </div>

          <div className={`grid grid-cols-1 ${type === "arithmetic" ? "lg:grid-cols-2" : "lg:grid-cols-[minmax(0,1fr)_auto]"} gap-6 lg:gap-10 items-start`}>
            {/* LEFT: prompt + input area */}
            <div className="p-6 sm:p-8 bg-white rounded-3xl card-tactile">
              {type === "arithmetic" && (
                <>
                  <div className="text-xs font-bold uppercase tracking-wider text-pebble mb-4">Solve</div>
                  <div data-testid="question-block" className="mx-auto max-w-[220px] font-heading text-6xl sm:text-7xl font-bold text-ink text-right leading-tight tabular-nums">
                    <div>{q.starting_number}</div>
                    {q.operations.map((op, i) => (
                      <div key={i}>
                        <span className={op.op === "+" ? "text-leaf" : op.op === "-" ? "text-watermelon" : op.op === "×" ? "text-sky" : "text-sky-hover"}>{op.op}</span>{op.value}
                      </div>
                    ))}
                    <div className="border-t-4 border-ink mt-2 pt-2">?</div>
                  </div>
                  <div className="mt-8">
                    <label htmlFor="answer-input" className="block text-sm font-bold text-ink mb-2 text-center">Your answer</label>
                    <input
                      id="answer-input"
                      data-testid="answer-input"
                      inputMode="numeric"
                      pattern="-?[0-9]*"
                      value={typedAnswer}
                      onChange={(e) => setTypedAnswer(e.target.value.replace(/[^0-9-]/g, ""))}
                      onKeyDown={(e) => { if (e.key === "Enter") goNext(); }}
                      placeholder="?"
                      className="w-full h-20 text-center text-5xl font-heading font-bold text-ink rounded-2xl border-2 border-cloud focus:border-sky focus:outline-none tabular-nums bg-canvas"
                    />
                  </div>
                </>
              )}

              {type === "read" && (
                <>
                  <div className="text-xs font-bold uppercase tracking-wider text-pebble mb-4">Show this number on the abacus</div>
                  <div data-testid="target-number" className="text-center font-heading text-7xl sm:text-8xl font-bold text-ink tabular-nums my-6">
                    {q.starting_number}
                  </div>
                  <div className="p-4 rounded-2xl bg-canvas text-center">
                    <div className="text-xs font-bold uppercase tracking-wider text-pebble">Your abacus shows</div>
                    <div data-testid="read-current" className={`font-heading text-4xl font-bold tabular-nums ${Number(abacusValue) === q.starting_number ? "text-leaf" : "text-ink"}`}>
                      {abacusValue}
                    </div>
                  </div>
                </>
              )}

              {type === "represent" && (
                <>
                  <div className="text-xs font-bold uppercase tracking-wider text-pebble mb-4">What number is on the abacus?</div>
                  <div className="text-center text-stone text-sm mb-4">Look at the beads on the right and type the number.</div>
                  <div className="mt-2">
                    <label htmlFor="answer-input" className="block text-sm font-bold text-ink mb-2 text-center">Your answer</label>
                    <input
                      id="answer-input"
                      data-testid="answer-input"
                      inputMode="numeric"
                      pattern="-?[0-9]*"
                      value={typedAnswer}
                      onChange={(e) => setTypedAnswer(e.target.value.replace(/[^0-9-]/g, ""))}
                      onKeyDown={(e) => { if (e.key === "Enter") goNext(); }}
                      placeholder="?"
                      className="w-full h-20 text-center text-5xl font-heading font-bold text-ink rounded-2xl border-2 border-cloud focus:border-sky focus:outline-none tabular-nums bg-canvas"
                    />
                  </div>
                </>
              )}

              <div className="mt-6 flex items-center gap-3">
                <button data-testid="prev-question-button" onClick={goPrev} disabled={current === 0} className="h-14 px-5 rounded-full bg-white border-2 border-cloud text-ink font-bold btn-tactile hover:border-sky disabled:opacity-40">Back</button>
                <button data-testid="next-question-button" onClick={goNext} disabled={submitting} className="flex-1 h-14 inline-flex items-center justify-center gap-2 rounded-full bg-watermelon text-white font-heading font-bold text-lg btn-tactile hover:bg-watermelon-hover disabled:opacity-70">
                  {current + 1 === total ? (<><CheckCircle2 className="w-5 h-5" /> Submit</>) : (<>Next <ArrowRight className="w-5 h-5" /></>)}
                </button>
              </div>
            </div>

            {/* RIGHT: abacus */}
            <div className="p-4 sm:p-6 bg-white rounded-3xl card-tactile">
              <div className="text-xs font-bold uppercase tracking-wider text-pebble mb-3">
                {type === "represent" ? "Read the beads" : "Virtual Soroban"}
              </div>
              <VirtualAbacus
                key={`${q.id}-${type}`}
                ref={abacusRef}
                rods={abacusRods}
                onesRodIndex={abacusOnes}
                readOnly={abacusReadOnly}
                initialValue={abacusInitial}
                onValueChange={setAbacusValue}
              />
              {type === "read" && <p className="mt-4 text-xs text-stone text-center">Tap the beads until the abacus shows the target number.</p>}
              {type === "arithmetic" && <p className="mt-4 text-xs text-stone text-center">Use the abacus to calculate, then type your final answer on the left.</p>}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
